import { ObjectUpdateData, OperationType } from "api-client";
import React from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { MEStore } from "features/MappingEditor/MappingEditorStore";
import { searchStore } from "features/ProductSearch/SearchStore";
import {
  IconLinkOff,
  IconSearch,
  IconPlayerPlay,
  IconRefresh,
} from "@tabler/icons-react";
import useFeatureFlag from "hooks/useFeatureFlag";
import { rulesSetStore } from "features/RulesSet/RulesSetStore";
import { buildingStore } from "store/Building/BuildingStore";
import useMappingEditor from "features/MappingEditor/useMappingEditor";
import { dynamicTableStore } from "features/DynamicTable/DynamicTableStore";

export interface MenuItem {
  id: string;
  title?: string;
  items?: MenuItem[];
  label?: string | null;
  icon?: React.ReactNode;
  tooltip?: string;
  children?: MenuItem[];
  value?: string | number | null;
  onClick?: () => void;
  hidden?: boolean;
  key?: string;
}

const editableObjectKeys = new Set<string>([
  "physical_object.material_flow_status",
  "physical_object.component",
  "physical_object.material",
  "physical_object.material_flow_status",
  "physical_object.length",
  "physical_object.width",
  "physical_object.height",
  "physical_object.area",
  "physical_object.volume",
  "physical_object.object_mass",
  "physical_object.cpx_deconstructability.qualification",
  "physical_object.cpx_preuse.qualification",
  "physical_object.cpx_separability.qualification",
  "physical_object.cpx_reusability.qualification",
]);

const useBuildActionBar = () => {
  const { ifc_id } = useParams();
  const { t, i18n } = useTranslation();
  const isEng = i18n.language === "en";
  const { isFeatureVisible } = useFeatureFlag();
  const { mappingToObjects, mappingToFile } = useMappingEditor();

  const handleOpenProductSearch = () => {
    MEStore.setCurrentRow(undefined);
    MEStore.setMatchForSelectedItems(true);
    searchStore.setOpenProductSearch({ state: true });
    MEStore.setOpenBulkActionsMenu(false);
  };

  const handleMapping = async (
    mode: OperationType,
    object_update_data?: ObjectUpdateData
  ) => {
    if (MEStore.selectAllChecked) {
      const confirmedAndMapped = await mappingToFile(
        mode,
        null,
        object_update_data
      );
      if (confirmedAndMapped) {
        await dynamicTableStore.fetchObjectsList(0, true);
      }
    } else {
      await mappingToObjects(mode, null, null, object_update_data);
    }
    MEStore.setOpenBulkActionsMenu(false);
  };

  const openUpdateMappingsModal = () => {
    MEStore.setOpenUpdateMappingsModal(true);
  };

  const isIFCFile = () => {
    return (
      buildingStore.sourceFiles.items.find((item) => item.id === ifc_id)
        ?.file_type === "IFC"
    );
  };

  const EDIT_MAPPING_ITEMS = [
    {
      id: "1_1",
      label: t("mappingEditor.manualMapping"),
      icon: <IconSearch size={19} />,
      onClick: () => handleOpenProductSearch(),
    },
    {
      hidden: !MEStore.selectAllChecked,
      id: "1_2",
      label: t("mappingEditor.rulesBasedMapping"),
      icon: <IconPlayerPlay size={19} />,
      onClick: () =>
        rulesSetStore.isThereAnyValidRule()
          ? handleMapping("rule_based_mapping")
          : rulesSetStore.setOpenNoRulePopup(true),
    },
    {
      id: "1_3",
      label: t("mappingEditor.undoMapping"),
      icon: <IconLinkOff size={19} />,
      onClick: () => handleMapping(OperationType.UndoMatch),
    },
    {
      hidden: !isFeatureVisible("update_mappings"),
      id: "1_4",
      label: t("mappingEditor.updateMappings"),
      icon: <IconRefresh size={19} />,
      onClick: () => openUpdateMappingsModal(),
      tooltip: t("mappingEditor.updateMappingsTooltip"),
    },
  ] as MenuItem[];

  const EDIT_MAPPING_MENU = {
    id: "1",
    title: t("mappingEditor.editProductMapping"),
    items: EDIT_MAPPING_ITEMS.filter((rec) => !rec?.hidden),
  };

  const objectProperties = dynamicTableStore.dynamicColumns.filter((e) =>
    editableObjectKeys.has(e.key_name)
  );

  const openEditObjectPopup = (
    columnKey: string | undefined,
    updateKey: keyof ObjectUpdateData | undefined,
    type: "string" | "select" | "number"
  ) => {
    MEStore.setOpenBulkActionsMenu(false);
    MEStore.setOpenEditObjectPopup({
      state: true,
      updateKey: updateKey,
      columnKey: columnKey,
      type: type,
      items: [],
    });
  };

  const getObjectByKey = (
    column_key: string,
    type: "string" | "select" | "number",
    updateKey?: keyof ObjectUpdateData | undefined
  ) => {
    const founded = objectProperties?.find(
      (rec) => rec.key_name === column_key
    );
    if (!founded) return {};
    return {
      id: String(founded?.id),
      label: isEng
        ? founded?.friendly_name.split("(")[0]
        : founded?.friendly_name_de.split("(")[0],
      onClick: () => openEditObjectPopup(column_key, updateKey, type),
      key: updateKey,
    } as MenuItem;
  };

  const generalInformationList = isIFCFile()
    ? [
        getObjectByKey(
          "physical_object.material_flow_status",
          "select",
          "material_flow_status"
        ),
      ]
    : [
        getObjectByKey("physical_object.component", "string", "component_name"),
        getObjectByKey("physical_object.material", "string", "material"),
        getObjectByKey(
          "physical_object.material_flow_status",
          "select",
          "material_flow_status"
        ),
      ];

  const ObjectsList = [
    {
      id: "generalInformations",
      title: t("mappingEditor.generalInformations"),
      items: generalInformationList,
    },
    {
      id: "dimensions",
      title: t("mappingEditor.dimensions"),
      items: [
        getObjectByKey("physical_object.length", "number", "length"),
        getObjectByKey("physical_object.width", "number", "width"),
        getObjectByKey("physical_object.height", "number", "height"),
        getObjectByKey("physical_object.area", "number", "area"),
        getObjectByKey("physical_object.volume", "number", "volume"),
        getObjectByKey("physical_object.object_mass", "number", "object_mass"),
      ],
      hidden: isIFCFile(),
    },
    {
      id: "circularity",
      title: t("mappingEditor.circularity"),
      items: [
        getObjectByKey(
          "physical_object.cpx_deconstructability.qualification",
          "select",
          "cpx_deconstructability_id"
        ),
        getObjectByKey(
          "physical_object.cpx_preuse.qualification",
          "select",
          "cpx_preuse_id"
        ),
        getObjectByKey(
          "physical_object.cpx_separability.qualification",
          "select",
          "cpx_separability_id"
        ),
        getObjectByKey(
          "physical_object.cpx_reusability.qualification",
          "select",
          "cpx_reusability_id"
        ),
      ],
    },
  ] as MenuItem[];

  const EDIT_OBJECT_MENU = {
    id: "3",
    title: t("mappingEditor.editObject"),
    items: [
      {
        id: "3_1",
        label: t("mappingEditor.editObjectProperty"),
        children: ObjectsList.filter((o) => !o.hidden),
      },
      {
        id: "3_2",
        label: t("mappingEditor.ignoreObjects"),
        onClick: () => handleMapping("ignore_objects"),
        hidden: !isFeatureVisible("ignore_objects"),
      },
      {
        id: "3_3",
        label: t("mappingEditor.unignoreObjects"),
        onClick: () => handleMapping("unignore_objects"),
        hidden: !isFeatureVisible("ignore_objects"),
      },
    ].filter((item) => !item.hidden),
    hidden: !isFeatureVisible("update_objects"),
  };

  const menuListOptions: MenuItem[] = [EDIT_MAPPING_MENU, EDIT_OBJECT_MENU];

  const menuList = menuListOptions.filter((rec) => !rec.hidden);

  return {
    handleOpenProductSearch,
    handleMapping,
    menuList,
  };
};

export default useBuildActionBar;
